/**
 * AuthProvider (TMMAPP-0003). Publishes the in-memory auth store's state changes to React. The
 * store is the source of truth for the token; this provider only mirrors "is there a session"
 * and "did it expire at runtime" so gates and screens can render accordingly.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi } from '@/api/auth';
import { env } from '@/config/env';
import { centralizedLoginUrl } from './silentRefresh';
import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
  type SignOutOptions,
} from './authContextValue';
import { getAccessToken, registerSignOutCallback, signOut as storeSignOut } from './authStore';

const SIGNED_OUT_PATH = '/auth/signed-out';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() => (getAccessToken() ? 'authed' : 'booting'));
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    registerSignOutCallback((expired) => {
      setStatus('unauthed');
      setSessionExpired(expired);
    });
    return () => registerSignOutCallback(null);
  }, []);

  const setAuthed = useCallback(() => {
    setStatus('authed');
    setSessionExpired(false);
  }, []);

  const signOut = useCallback(async (options?: SignOutOptions) => {
    try {
      await authApi.logout();
    } catch {
      /* best effort */
    }
    storeSignOut();
    // A full navigation clears every in-memory state, the session included.
    const sharedPath = options?.sharedReturnTo;
    window.location.assign(
      sharedPath && /^\/shared\/\d+$/.test(sharedPath)
        ? centralizedLoginUrl(`${env.appOrigin}${sharedPath}`)
        : options?.deleted !== undefined
          ? `${SIGNED_OUT_PATH}?deleted=${options.deleted}`
          : SIGNED_OUT_PATH,
    );
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, sessionExpired, setAuthed, signOut }),
    [status, sessionExpired, setAuthed, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
